import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { devApi, prodDevApi } from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IResetSecretKeyRequest {
  id: string;
  environment: string;
}

export interface IResetSecretKeyResponse {
  status?: string;
  message?: string;
  statusCode?: string;
  data?: {
    testSecretKey?: string;
    liveSecretKey?: string;
    id?: string;
    name?: string;
    isActivated?: boolean;
    internalBusinessId?: string;
    customerId?: string;
    collectionAccountNumber?: string;
    liveWebhookUrl?: string;
    alias?: string;
    status?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IResetSecretKeyResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const resetSecretKeyAsync = createAsyncThunk(
  'secretKey/reset',
  async (
    { id, environment }: IResetSecretKeyRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const devTokenInStorage = localStorage.getItem('devTokenData');

      const parsedDevTokenInStorage = devTokenInStorage
        ? JSON.parse(devTokenInStorage)
        : null;

      const businessId = localStorage.getItem('businessId');

      const environment = state.environment?.value;

      const apiInstance = environment === 'live' ? prodDevApi : devApi;

      const authToken =
        environment === 'live' ? accessToken : parsedDevTokenInStorage;

      const config = {
        headers: {
          Authorization: `Token ${authToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await apiInstance.put(
        `businesses/${id}/keys`,
        { environment: 'live' },
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const secretKeyResetSlice = createSlice({
  name: 'secretKey/resetKey',
  initialState,
  reducers: {
    resetSecretKeyReset: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetSecretKeyAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(resetSecretKeyAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IResetSecretKeyResponse;
      })
      .addCase(resetSecretKeyAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IResetSecretKeyResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetSecretKeyReset } = secretKeyResetSlice.actions;

export default secretKeyResetSlice.reducer;
